import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { WidgetTrendChartSkeleton } from '@/components/common/WidgetTrendChartSkeleton';
import { axisClasses, BarChart } from '@mui/x-charts';
import dayjs from '@/config/dayjs';
import { useNavigate } from 'react-router';

import { FooterButton } from '@/components/common/FooterButton';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { MonthlyNetWorthInterface } from '@/interfaces/user';
import {
  formatSeriesTooltipCurrency,
  getCurrencyStringTitleForChart,
} from '@/utils/currency';
import {
  formatChartDateAxis,
  responsiveTickInterval,
} from '@/utils/charts/axisFormatters';
import ChartCurrencyTitle from '@/components/charts/chartCurrencyTitle';
import { useLazyGetAccountsHistoricalQuery } from '@/store/accountsApi';
import { WARootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import DateRange from '@/components/date/DateRange';
import { useControlledDates } from '@/hooks/useControlledDates';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { MonthlyNetWorthChartInterface } from '@/models/AccountViewModel';
import { useLocalization } from '@/hooks/useLocalization';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { useGraphColors } from '@/hooks/useGraphColors';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { dashboardMobileWidthValue } from '@/constants';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { useViewportWidth } from '@/hooks/useViewportWidth';
import { ContentContainer } from '../contentContainer';
import { buildMonthlyNetWorth } from './dashboardNetWorthData';

interface DashboardNetWorthInterface extends StandardWidgetProps {
  monthlyNetWorth?: Array<MonthlyNetWorthInterface>;
  totalNetWorth?: number;
}

const { startDate: initialStartDate, endDate: initialEndDate } =
  getDateRangeFromFilter(DataRangeFilterEnum.Trailing6Months);

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

const HISTORY_LOOKBACK_MONTHS = 12;
const SIGNED_TICK_AXIS_WIDTH = 55;

function getMonthEndDates(startDate: string, endDate: string) {
  const dates = [];
  let currentDate = dayjs(startDate).startOf('month');
  const lastDate = dayjs(endDate);

  while (
    currentDate.isBefore(lastDate) ||
    currentDate.isSame(lastDate, 'month')
  ) {
    if (currentDate.isSame(lastDate, 'month')) {
      dates.push(lastDate.format('YYYY-MM-DD'));
    } else {
      dates.push(currentDate.endOf('month').format('YYYY-MM-DD'));
    }
    currentDate = currentDate.add(1, 'month');
  }

  return dates;
}

export default function DashboardNetWorth({
  startDate: propStartDate,
  endDate: propEndDate,
  isWidget = false,
  onDataLoaded,
  title,
  container = 'widget',
}: DashboardNetWorthInterface) {
  const authIsLoaded = useAuthIsLoaded();
  const { t } = useLocalization();
  const graphColors = useGraphColors();
  const navigate = useNavigate();
  const viewportWidth = useViewportWidth();
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );
  const canViewAccounts = useCanNavigateTo('/investor/Portfolio/accounts');
  const effectiveExclusions = useEffectiveExclusions();
  const { ref: widgetWidthRef, isBelow: isWidgetBelowNarrowDateField } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const widgetNarrowDateField = Boolean(
    isWidget && isWidgetBelowNarrowDateField,
  );
  const widgetTitle =
    title ??
    (currentRole?.toLowerCase() === 'owner'
      ? t('total')
      : t('historicalNetWorth'));

  const [chartData, setChartData] = useState<MonthlyNetWorthChartInterface[]>(
    [],
  );
  const [isChartUpdating, setIsChartUpdating] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  const { startDate, endDate, setStartDate, setEndDate } = useControlledDates({
    propStartDate,
    propEndDate,
    initialWidgetStartDate: initialStartDate,
    initialWidgetEndDate: initialEndDate,
    isWidget,
  });

  const [getHistoricalTrend] = useLazyGetAccountsHistoricalQuery();

  useEffect(() => {
    if (!authIsLoaded) {
      return;
    }

    if (!startDate || !endDate) {
      setIsChartUpdating(false);
      return;
    }

    let cancelled = false;

    const fetchMonthlyData = async () => {
      setIsChartUpdating(true);
      setHasLoadError(false);

      const notifyDataLoaded = () => {
        if (onDataLoaded) {
          setTimeout(() => {
            onDataLoaded();
          }, 2000);
        }
      };

      try {
        const monthEndDates = getMonthEndDates(startDate, endDate);
        if (monthEndDates.length === 0) {
          setChartData([]);
          notifyDataLoaded();
          return;
        }

        const rangeStart = dayjs(startDate)
          .subtract(HISTORY_LOOKBACK_MONTHS, 'month')
          .format('YYYY-MM-DD');
        const rangeEnd = monthEndDates[monthEndDates.length - 1];
        const fetchTrend = async (trendType: string) =>
          getHistoricalTrend(
            {
              startDate: rangeStart,
              endDate: rangeEnd,
              trendType,
              ...(effectiveExclusions.excludedAccounts.length > 0
                ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
                : {}),
              ...(effectiveExclusions.excludedAccountTypes.length > 0
                ? {
                    ExcludedAccountTypes:
                      effectiveExclusions.excludedAccountTypes,
                  }
                : {}),
              ...(effectiveExclusions.excludedCategories.length > 0
                ? { ExcludedCategories: effectiveExclusions.excludedCategories }
                : {}),
              ...(effectiveExclusions.excludedInstitutions.length > 0
                ? {
                    ExcludedInstitutions:
                      effectiveExclusions.excludedInstitutions,
                  }
                : {}),
            },
            true,
          ).unwrap();

        let series;
        try {
          series = await Promise.all([
            fetchTrend('combined'),
            fetchTrend('deposits'),
            fetchTrend('liabilities'),
          ]);
        } catch {
          series = null;
        }

        if (cancelled) {
          return;
        }

        if (series) {
          const [combined, deposits, liabilities] = series;
          setChartData(
            buildMonthlyNetWorth(monthEndDates, {
              combined,
              deposits,
              liabilities,
            }),
          );
        } else {
          setChartData([]);
          setHasLoadError(true);
        }

        notifyDataLoaded();
      } finally {
        if (!cancelled) {
          setIsChartUpdating(false);
        }
      }
    };

    void fetchMonthlyData();

    return () => {
      cancelled = true;
    };
  }, [
    authIsLoaded,
    startDate,
    endDate,
    getHistoricalTrend,
    onDataLoaded,
    effectiveExclusions,
  ]);

  const hasNegativeNetWorth = chartData.some((row) => row.netWorth < 0);

  const getChart = () => (
    <BarChart
      height={340}
      margin={{ left: 0, right: 40, top: 10, bottom: 40 }}
      yAxis={[
        {
          ...(hasNegativeNetWorth && { width: SIGNED_TICK_AXIS_WIDTH }),
          valueFormatter: (value: number | null) => {
            if (!value) {
              return getCurrencyStringTitleForChart(0);
            }

            return getCurrencyStringTitleForChart(value);
          },
        },
      ]}
      series={[
        {
          dataKey: 'netWorth',
          color: graphColors[0] ?? 'black',
          label: t('netWorth'),
          minBarSize: 1,
        },
        {
          dataKey: 'assets',
          color: graphColors[1] ?? 'black',
          label: t('assets'),
        },
        {
          dataKey: 'liabilities',
          color: graphColors[2] ?? 'black',
          label: t('liabilities'),
        },
      ].map((x) => {
        return {
          ...x,
          valueFormatter: (value: number | null) =>
            formatSeriesTooltipCurrency(value),
        };
      })}
      dataset={chartData}
      xAxis={[
        {
          scaleType: 'band',
          dataKey: 'date',
          height: 50,
          valueFormatter: (value: Date | null, ctx) =>
            formatChartDateAxis(value, ctx),
          tickLabelInterval: responsiveTickInterval(viewportWidth),
        },
      ]}
      sx={{
        [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
          transform: 'translateX(-10px)',
        },
      }}
      borderRadius={8}
    />
  );

  const finalNetWorth =
    !isChartUpdating && chartData.length > 0
      ? chartData[chartData.length - 1].netWorth
      : 0;

  const initialNetWorth =
    !isChartUpdating && chartData.length > 0 ? chartData[0].netWorth : 0;

  const netChange = finalNetWorth - initialNetWorth;

  const percentageChange =
    initialNetWorth !== 0 ? netChange / initialNetWorth : 0;

  const disableDatePicker =
    isEmbeddableBundle && (isChartUpdating || !authIsLoaded);

  // Use the end date for "as of" date
  const getAsOfDate = () => {
    if (endDate) {
      return dayjs(endDate).format('MM/DD/YYYY');
    }
    return '';
  };

  return (
    <ContentContainer
      ref={widgetWidthRef}
      sx={{ minWidth: { xs: '100%', sm: 350 }, textAlign: 'center' }}
      container={container}
      hasCustomPadding
    >
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'top-center',
            mb: 2,
          }}
        >
          <Typography variant='h5'>{widgetTitle}</Typography>
          {isWidget && (
            <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
              <DateRange
                startDate={startDate}
                endDate={endDate}
                onChange={(startDate, endDate, _shortcutLabel) => {
                  setStartDate(startDate);
                  setEndDate(endDate);
                }}
                useWidgetNarrowIconField={widgetNarrowDateField}
                disabled={disableDatePicker}
              />
            </Box>
          )}
        </Box>
        {isChartUpdating || !authIsLoaded ? (
          <WidgetTrendChartSkeleton />
        ) : (
          <>
            {hasLoadError ? (
              <Typography color='error'>{t('errorLoadingData')}</Typography>
            ) : (
              <>
                <ChartCurrencyTitle
                  totalValue={finalNetWorth}
                  otherValue={netChange}
                  percentage={percentageChange}
                  compareText={`as of ${getAsOfDate()}`}
                />
                {getChart()}
              </>
            )}
          </>
        )}
      </Box>
      {canViewAccounts && !isEmbeddableBundle && (
        <FooterButton
          buttonText={t('viewAccounts')}
          onClick={() => {
            void navigate('/investor/Portfolio/accounts');
          }}
        />
      )}
    </ContentContainer>
  );
}
